import { HStack, Text, VStack, useColorMode } from '@chakra-ui/react';
import { ToolbarProps } from 'react-big-calendar';
import {
	useCalendarSettings,
	useAutoContrastColor,
	useShortcodeSettings,
	anyFilterEnabled,
	styles,
} from '../../../lib';
import { ChevronLeft, ChevronRight } from '../../icons';
import { formatNavigation, ViewMenu } from '.';
import { FiltersDrawer } from './FiltersDrawer';
import { useToolbarLogic } from './useToolbarLogic';
import { DateInputField } from './DateInputField';
import { useMemo } from 'react';

export const MobileToolbar = ({ date, view, views, onNavigate, onView }: ToolbarProps) => {
	const {
		inputValue,
		inputType,
		handlePrevClick,
		handleNextClick,
		handleTodayClick,
		handleDateInputChange,
		createViewChangeHandler,
		viewLabels,
	} = useToolbarLogic(date, view, onNavigate, onView);

	const { settings } = useCalendarSettings();
	const { settings: shortcodeSettings } = useShortcodeSettings();

	const { colorMode } = useColorMode();
	const { color: contrastColor, ref } = useAutoContrastColor();
	const { formattedPrev, formattedNext, todayText } = formatNavigation(date, view);

	const { calendarToolbarBackgroundColor, calendarTextColor } = settings.styles[colorMode];
	const { iconStyle } = styles.toolbar;
	const { enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter } =
		settings;

	// Checks for rendering filters through settings/shortcodes
	const shortcodeFiltersAllowed = shortcodeSettings.filters !== false;
	const filtersExplicitlyEnabled = shortcodeSettings.filters === true;
	const anyFiltersEnabled = useMemo(
		() =>
			anyFilterEnabled(
				enableSearchFilter,
				enableLocationFilter,
				enableCategoryFilter,
				enableVenueFilter,
				enableTagFilter
			),
		[enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter]
	);
	return (
		<>
			<VStack
				className='calendar-toolbar'
				borderRadius={settings.radius.toolbar}
				backgroundColor={calendarToolbarBackgroundColor}
			>
				<HStack ref={ref} w='full' alignItems='center' justifyContent='space-between' px={1} py={2}>
					<HStack onClick={handlePrevClick} cursor='pointer'>
						<ChevronLeft size={18} fill={contrastColor} style={iconStyle} />
						<Text marginInline='0px !important' marginBottom='0px !important' color={calendarTextColor}>
							{formattedPrev}
						</Text>
					</HStack>
					<Text onClick={handleTodayClick} color={contrastColor} cursor='pointer'>
						{todayText}
					</Text>
					<HStack onClick={handleNextClick} cursor='pointer'>
						<Text marginInline='0px !important' marginBottom='0px !important' color={calendarTextColor}>
							{formattedNext}
						</Text>
						<ChevronRight size={18} fill={contrastColor} style={iconStyle} />
					</HStack>
				</HStack>
				<HStack w='full' marginTop={0}>
					<DateInputField inputType={inputType} inputValue={inputValue} onChange={handleDateInputChange} />
					{shortcodeFiltersAllowed && (anyFiltersEnabled || filtersExplicitlyEnabled) && <FiltersDrawer />}
					<ViewMenu views={views} createViewChangeHandler={createViewChangeHandler} viewLabels={viewLabels} />
				</HStack>
			</VStack>
		</>
	);
};
